import { useRef, Suspense } from 'react';
import { useThree, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import { AppleLogo } from './AppleLogo';
import { ImagePlane } from './ImagePlane';

// Preload images
useLoader.preload(THREE.TextureLoader, '/intro-3.jpg');
useLoader.preload(THREE.TextureLoader, '/intro-4.jpg');

function Images({ image3Opacity, image4Opacity, slideOffset }) {
  return (
    <>
      <ImagePlane
        imageSrc="/intro-3.jpg"
        opacity={image3Opacity}
        positionX={slideOffset}
      />
      <ImagePlane
        imageSrc="/intro-4.jpg"
        opacity={image4Opacity}
        positionX={slideOffset}
      />
    </>
  );
}

export function IntroScene({ currentStage, stageProgress }) {
  const groupRef = useRef();
  const { viewport } = useThree();

  const getStageValues = () => {
    switch (currentStage) {
      case 0:
        return {
          blackOpacity: 1,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 0,
          image4Opacity: 0,
          slideOffset: 0,
        };
      case 1:
        return {
          blackOpacity: 1,
          logoOpacity: stageProgress,
          logoGlow: stageProgress * 0.8,
          image3Opacity: 0,
          image4Opacity: 0,
          slideOffset: 0,
        };
      case 2:
        return {
          blackOpacity: 1,
          logoOpacity: 1,
          logoGlow: 0.8,
          image3Opacity: 0,
          image4Opacity: 0,
          slideOffset: 0,
        };
      case 3:
        return {
          blackOpacity: 1 - stageProgress,
          logoOpacity: 1 - stageProgress,
          logoGlow: 0.8 * (1 - stageProgress),
          image3Opacity: stageProgress,
          image4Opacity: 0,
          slideOffset: 0,
        };
      case 4:
        return {
          blackOpacity: 0,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 1,
          image4Opacity: 0,
          slideOffset: 0,
        };
      case 5:
        return {
          blackOpacity: 0,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 1 - stageProgress,
          image4Opacity: stageProgress,
          slideOffset: 0,
        };
      case 6:
        return {
          blackOpacity: 0,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 0,
          image4Opacity: 1,
          slideOffset: 0,
        };
      case 7:
        return {
          blackOpacity: 0,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 0,
          image4Opacity: 1,
          slideOffset: -stageProgress * viewport.width * 0.302,
        };
      case 8:
        return {
          blackOpacity: 0,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 0,
          image4Opacity: 1,
          slideOffset: -viewport.width * 0.302,
        };
      default:
        return {
          blackOpacity: 1,
          logoOpacity: 0,
          logoGlow: 0,
          image3Opacity: 0,
          image4Opacity: 0,
          slideOffset: 0,
        };
    }
  };

  const values = getStageValues();

  return (
    <group ref={groupRef}>
      {/* Images layer (behind everything when opacity is 0, visible when opacity > 0) */}
      <Suspense fallback={null}>
        <Images
          image3Opacity={values.image3Opacity}
          image4Opacity={values.image4Opacity}
          slideOffset={values.slideOffset}
        />
      </Suspense>

      {/* Black background overlay */}
      {values.blackOpacity > 0 && (
        <mesh position={[0, 0, 0]}>
          <planeGeometry args={[viewport.width * 3, viewport.height * 3]} />
          <meshBasicMaterial
            color="#000000"
            transparent
            opacity={values.blackOpacity}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Apple Logo (on top of black background) */}
      {values.logoOpacity > 0 && (
        <group position={[0, 0, 0.1]}>
          <AppleLogo
            opacity={values.logoOpacity}
            scale={0.95}
            glowIntensity={values.logoGlow}
          />
        </group>
      )}
    </group>
  );
}
